// Copyright (c) 2025 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useState, useEffect } from 'react';
import { Container, Header, Segment, Statistic, Table, Message, Loader, Icon, Label } from 'semantic-ui-react';
import { loaAdminApi, TemplateUsageReport } from '../api/loaAdminApi';

/**
 * Phase 4: Template Preview & Reporting
 * Dashboard showing template usage analytics across all LOAs
 */
function TemplateUsageDashboardPage() {
  const [report, setReport] = useState<TemplateUsageReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadReport = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await loaAdminApi.getTemplateUsageReport();
        setReport(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load template usage report');
      } finally {
        setLoading(false);
      }
    };

    loadReport();
  }, []);

  if (loading) {
    return (
      <Container style={{ marginTop: '2em' }}>
        <Loader active>Loading template usage report...</Loader>
      </Container>
    );
  }

  if (error) {
    return (
      <Container style={{ marginTop: '2em' }}>
        <Message negative>
          <Message.Header>Error Loading Report</Message.Header>
          <p>{error}</p>
        </Message>
      </Container>
    );
  }

  if (!report) {
    return null;
  }

  const trackingPercentage = report.totalLOAs > 0
    ? ((report.loasWithTemplateTracking / report.totalLOAs) * 100).toFixed(1)
    : '0';

  return (
    <Container style={{ marginTop: '2em' }}>
      <Header as="h1">
        <Icon name="chart bar" />
        <Header.Content>
          Template Usage Dashboard
          <Header.Subheader>Analytics on template usage across all Letters of Authority</Header.Subheader>
        </Header.Content>
      </Header>

      <Segment>
        <Header as="h3">
          <Icon name="info circle" />
          Overview Statistics
        </Header>
        <Statistic.Group widths="three">
          <Statistic>
            <Statistic.Value>{report.totalLOAs}</Statistic.Value>
            <Statistic.Label>Total LOAs</Statistic.Label>
          </Statistic>
          <Statistic>
            <Statistic.Value>{report.loasWithTemplateTracking}</Statistic.Value>
            <Statistic.Label>LOAs with Template Tracking</Statistic.Label>
          </Statistic>
          <Statistic color="blue">
            <Statistic.Value>{trackingPercentage}%</Statistic.Value>
            <Statistic.Label>Tracking Coverage</Statistic.Label>
          </Statistic>
        </Statistic.Group>
      </Segment>

      {report.templateUsageStats.length > 0 ? (
        <Segment>
          <Header as="h3">
            <Icon name="list" />
            Template Usage Breakdown
          </Header>
          <Table celled sortable>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Template Name</Table.HeaderCell>
                <Table.HeaderCell>Template ID</Table.HeaderCell>
                <Table.HeaderCell>Total Usage Count</Table.HeaderCell>
                <Table.HeaderCell>Unique LOA Count</Table.HeaderCell>
                <Table.HeaderCell>Usage Percentage</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {report.templateUsageStats.map((stat, index) => (
                <Table.Row key={stat.templateId}>
                  <Table.Cell>
                    <strong>{stat.templateName}</strong>
                    {index === 0 && (
                      <Label color="green" size="tiny" style={{ marginLeft: '0.5em' }}>
                        Most Used
                      </Label>
                    )}
                  </Table.Cell>
                  <Table.Cell>
                    <code>{stat.templateId}</code>
                  </Table.Cell>
                  <Table.Cell>
                    <Label color="blue">{stat.usageCount}</Label>
                  </Table.Cell>
                  <Table.Cell>
                    <Label color="teal">{stat.uniqueLOACount}</Label>
                  </Table.Cell>
                  <Table.Cell>
                    <Label color="grey">{stat.usagePercentage.toFixed(1)}%</Label>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </Segment>
      ) : (
        <Message info>
          <Message.Header>No Template Usage Data</Message.Header>
          <p>No LOAs with template tracking information found. Template tracking is automatically recorded when LOAs are generated using the template-based generation system.</p>
        </Message>
      )}

      <Message info>
        <Message.Header>
          <Icon name="lightbulb" /> About Template Usage Tracking
        </Message.Header>
        <p>
          <strong>Total Usage Count:</strong> Total number of times a template has been used across all policy inclusions in LOAs (one LOA can use the same template multiple times for different policies).
        </p>
        <p>
          <strong>Unique LOA Count:</strong> Number of distinct LOAs that use this template at least once.
        </p>
        <p>
          <strong>Usage Percentage:</strong> Percentage of total LOAs that use this template (based on unique LOA count).
        </p>
        <p>
          <em>Note: Only LOAs generated with template-based generation (Phase 2+) include tracking information. Legacy LOAs created before template tracking was implemented will not appear in these statistics.</em>
        </p>
      </Message>
    </Container>
  );
}

export default TemplateUsageDashboardPage;
